import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLangBar from "./AdminLangBar";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

const inputCls =
  "w-full bg-transparent border-b border-[#E5DFD5] py-2.5 text-sm focus:outline-none focus:border-[#C5A880]";

const FieldLabel = ({ children }) => (
  <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
    {children}
  </label>
);

const DEFAULTS = {
  title: "Header",
  brand_name: "Finca Cortesín",
  brand_subtitle: "New Developments",
  nav_developments: "Developments",
  nav_explore: "Explore",
  nav_about: "About",
  nav_blog: "News & Blog",
  nav_contact: "Contact",
};

export default function AdminHeaderEdit() {
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setPage(null);
    (async () => {
      try {
        const { data } = await api.get("/admin/pages/header", { params: { lang } });
        setPage({ ...DEFAULTS, ...data });
      } catch (_err) {
        setPage({ key: "header", ...DEFAULTS });
      }
    })();
  }, [lang, reloadTick]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { key: _, ...payload } = page;
      await api.put("/admin/pages/header", payload, { params: { lang } });
      toast.success("Header saved");
    } catch (_err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!page)
    return (
      <div className="p-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      </div>
    );

  const set = (k, v) => setPage({ ...page, [k]: v });

  return (
    <div className="p-10 lg:p-16 max-w-[900px]" data-testid="admin-header-edit">
      <Link
        to="/admin/pages"
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A] mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All Pages
      </Link>
      <h1
        className="text-4xl lg:text-5xl font-light mb-2"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Header
      </h1>
      <p className="text-sm text-[#73706B] mb-12">
        Site-wide navigation bar text. Visible on every public page.
      </p>

      <AdminLangBar lang={lang} onChange={setLang} kind="page" docKey={"header"} disabled={false} onTranslated={() => setReloadTick((n) => n + 1)} />

      <form onSubmit={save} className="space-y-10">
        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Brand</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FieldLabel>Brand name</FieldLabel>
              <input
                value={page.brand_name || ""}
                onChange={(e) => set("brand_name", e.target.value)}
                className={inputCls}
                data-testid="header-field-brand-name"
              />
            </div>
            <div>
              <FieldLabel>Brand subtitle</FieldLabel>
              <input
                value={page.brand_subtitle || ""}
                onChange={(e) => set("brand_subtitle", e.target.value)}
                className={inputCls}
                data-testid="header-field-brand-subtitle"
              />
            </div>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Navigation labels</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FieldLabel>Developments</FieldLabel>
              <input
                value={page.nav_developments || ""}
                onChange={(e) => set("nav_developments", e.target.value)}
                className={inputCls}
                data-testid="header-field-nav-developments"
              />
            </div>
            <div>
              <FieldLabel>Explore</FieldLabel>
              <input
                value={page.nav_explore || ""}
                onChange={(e) => set("nav_explore", e.target.value)}
                className={inputCls}
                data-testid="header-field-nav-explore"
              />
            </div>
            <div>
              <FieldLabel>About</FieldLabel>
              <input
                value={page.nav_about || ""}
                onChange={(e) => set("nav_about", e.target.value)}
                className={inputCls}
                data-testid="header-field-nav-about"
              />
            </div>
            <div>
              <FieldLabel>News &amp; Blog</FieldLabel>
              <input
                value={page.nav_blog || ""}
                onChange={(e) => set("nav_blog", e.target.value)}
                className={inputCls}
                data-testid="header-field-nav-blog"
              />
            </div>
            <div>
              <FieldLabel>Contact</FieldLabel>
              <input
                value={page.nav_contact || ""}
                onChange={(e) => set("nav_contact", e.target.value)}
                className={inputCls}
                data-testid="header-field-nav-contact"
              />
            </div>
          </div>
        </section>

        <div className="flex items-center gap-4 border-t border-[#E5DFD5] pt-8">
          <button
            type="submit"
            disabled={saving}
            data-testid="header-save"
            className="bg-[#1A1A1A] text-[#F9F6F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
            {saving ? "Saving" : "Save Header"}
          </button>
        </div>
      </form>
    </div>
  );
}
